import React from "react";
import { motion } from "framer-motion";
import { FaImages, FaMapMarkerAlt } from "react-icons/fa";
import ProjectPicture from "./ProjectPicture";
import { isInProgress } from "./projects";

function ProjectCard({ project, index, onOpen }) {
  const cover = project.images[0];
  const subline = [project.location, project.year].filter(Boolean).join(" · ");

  return (
    <motion.li
      initial={{ opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.1 }}
      transition={{ duration: 0.4, delay: Math.min(index, 6) * 0.05 }}
      className="h-full"
    >
      <article className="group relative flex h-full w-full flex-col overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-md transition-all duration-300 hover:-translate-y-1 hover:border-brand-yellow hover:shadow-xl focus-within:border-brand-yellow">
        <div className="relative aspect-[4/3] w-full overflow-hidden bg-gray-200">
          <ProjectPicture
            project={project}
            image={cover}
            alt=""
            sizes="(min-width: 1280px) 400px, (min-width: 1024px) 33vw, (min-width: 640px) 50vw, 100vw"
            loading={index < 6 ? "eager" : "lazy"}
            fetchpriority={index === 0 ? "high" : undefined}
            className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105 motion-reduce:transition-none motion-reduce:group-hover:scale-100"
          />
          <span className="absolute left-3 top-3 flex flex-wrap gap-1.5">
            <span className="rounded-full bg-brand-navy/90 px-3 py-1 text-xs font-semibold text-white backdrop-blur-sm">
              {project.projectCategory}
            </span>
            {isInProgress(project) && (
              <span className="rounded-full bg-brand-yellow px-3 py-1 text-xs font-bold text-brand-navy">In Progress</span>
            )}
          </span>
          <span className="absolute bottom-3 right-3 inline-flex items-center gap-1.5 rounded-full bg-black/60 px-3 py-1 text-xs font-medium text-white backdrop-blur-sm">
            <FaImages aria-hidden="true" /> {project.images.length}
          </span>
          <span className="absolute inset-0 flex items-center justify-center bg-brand-navy/50 opacity-0 transition-opacity duration-300 group-hover:opacity-100 group-focus-within:opacity-100">
            <span className="rounded-full bg-brand-yellow px-5 py-2 text-sm font-bold text-brand-navy shadow-lg">
              View Photos
            </span>
          </span>
        </div>
        <div className="flex flex-1 flex-col p-5">
          <h2 className="line-clamp-2 text-base sm:text-lg font-bold text-gray-900 transition-colors group-hover:text-brand-navy">
            {project.title}
          </h2>
          {project.client && (
            <p className="mt-1.5 text-sm font-medium text-brand-navy-light line-clamp-1">{project.client}</p>
          )}
          {subline && (
            <p className="mt-2 inline-flex items-center gap-1.5 text-sm text-gray-500">
              <FaMapMarkerAlt aria-hidden="true" className="text-brand-navy-light" /> {subline}
            </p>
          )}
        </div>
        {/* Full-card click target, kept outside the content so the heading stays valid HTML */}
        <button
          type="button"
          onClick={() => onOpen(project)}
          aria-label={`View ${project.images.length} photos of ${project.title}`}
          className="peer absolute inset-0 z-10 rounded-2xl focus:outline-none focus-visible:ring-4 focus-visible:ring-brand-yellow"
        />
      </article>
    </motion.li>
  );
}

export default ProjectCard;
